import { useCallback } from 'react'

import { useMutation } from '@apollo/client/react'

import { toast } from '@buffer-mono/popcorn'

import { GetAccount } from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'
import { logError } from '~publish/legacy/utils/logError'

export const EnableBetaFeaturesMutation = graphql(/* GraphQL */ `
  mutation addOrganizationToBetaProgram($organizationId: String!) {
    addOrganizationToBetaProgram(organizationId: $organizationId) {
      ... on BasicError {
        userFriendlyMessage
        cause
      }
      ... on MutationBasicResponse {
        success
      }
    }
  }
`)

export const DisableBetaFeaturesMutation = graphql(/* GraphQL */ `
  mutation removeOrganizationFromBetaProgram($organizationId: String!) {
    removeOrganizationFromBetaProgram(organizationId: $organizationId) {
      ... on BasicError {
        userFriendlyMessage
        cause
      }
      ... on MutationBasicResponse {
        success
      }
    }
  }
`)

type UpdateBetaFeaturesEnrolment = ({
  organizationId,
}: {
  organizationId: string
}) => Promise<boolean>

type UpdateBetaFeaturesEnrolmentResponse = {
  enableBetaFeatures: UpdateBetaFeaturesEnrolment
  disableBetaFeatures: UpdateBetaFeaturesEnrolment
  isLoading: boolean
  hasError: boolean
}

export const RELOAD_TOAST_OPTIONS = {
  description:
    'It may take a couple of minutes for any changes to take full effect.',
  action: {
    label: 'Reload Now',
    onClick: (): void => window.location.reload(),
  },
} as const

/**
 * Custom hook to manage beta features enrollment for the current organization.
 * @returns {UpdateBetaFeaturesEnrolmentResponse} Functions and state for enabling/disabling beta features enrollment.
 */
export const useUpdateBetaFeaturesEnrolment =
  (): UpdateBetaFeaturesEnrolmentResponse => {
    const [executeEnableBetaFeatures, { loading, error }] = useMutation(
      EnableBetaFeaturesMutation,
    )
    const [
      executeDisableBetaFeatures,
      { loading: disableLoading, error: disableError },
    ] = useMutation(DisableBetaFeaturesMutation)

    const enableBetaFeatures = useCallback(
      async ({ organizationId }: { organizationId: string }) => {
        try {
          const { data, error: enableMutationError } =
            await executeEnableBetaFeatures({
              variables: {
                organizationId,
              },
              refetchQueries: [GetAccount],
            })

          // Handle GraphQL errors if any
          if (enableMutationError) {
            const unexpectedError = new Error(
              enableMutationError.message || 'Unexpected API response',
            )
            logError(unexpectedError, {
              metaData: { data, error: enableMutationError },
            })
            throw unexpectedError
          }
          const result = data?.addOrganizationToBetaProgram
          if (result?.__typename === 'BasicError') {
            throw new Error(result.userFriendlyMessage)
          }
          if (
            result?.__typename !== 'MutationBasicResponse' ||
            !result.success
          ) {
            throw new Error('Failed to add organization to beta program.')
          }

          toast.success(
            'Organization added to beta program',
            RELOAD_TOAST_OPTIONS,
          )

          return true
        } catch (enableCaughtError) {
          toast.error(
            'Sorry, an error occurred while adding your organization to the beta program. Please try again.',
          )
          logError(
            enableCaughtError instanceof Error
              ? enableCaughtError
              : new Error(String(enableCaughtError)),
            {
              metaData: { variables: { organizationId } },
            },
          )
          throw enableCaughtError
        }
      },
      [executeEnableBetaFeatures],
    )

    const disableBetaFeatures = useCallback(
      async ({ organizationId }: { organizationId: string }) => {
        try {
          const { data, error: disableMutationError } =
            await executeDisableBetaFeatures({
              variables: {
                organizationId,
              },
              refetchQueries: [GetAccount],
            })

          // Handle GraphQL errors if any
          if (disableMutationError) {
            const unexpectedError = new Error(
              disableMutationError.message || 'Unexpected API response',
            )
            logError(unexpectedError, {
              metaData: { data, error: disableMutationError },
            })
            throw unexpectedError
          }
          const removeResult = data?.removeOrganizationFromBetaProgram
          if (removeResult?.__typename === 'BasicError') {
            throw new Error(removeResult.userFriendlyMessage)
          }
          if (
            removeResult?.__typename !== 'MutationBasicResponse' ||
            !removeResult.success
          ) {
            throw new Error('Failed to remove organization from beta program.')
          }

          toast.success(
            'Organization removed from beta program',
            RELOAD_TOAST_OPTIONS,
          )

          return true
        } catch (disableCaughtError) {
          toast.error(
            'Sorry, an error occurred while removing your organization from the beta program. Please try again.',
          )
          logError(
            disableCaughtError instanceof Error
              ? disableCaughtError
              : new Error(String(disableCaughtError)),
            {
              metaData: { variables: { organizationId } },
            },
          )
          throw disableCaughtError
        }
      },
      [executeDisableBetaFeatures],
    )

    return {
      enableBetaFeatures,
      disableBetaFeatures,
      isLoading: loading || disableLoading,
      hasError: !!error || !!disableError,
    }
  }
